import classNames from 'classnames';
import {
  AnchorHTMLAttributes,
  forwardRef,
  MouseEventHandler,
  useMemo,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useAppendQueryParams } from '../utils/useAppendQueryParams';

interface FeLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {}

// List of keywords commonly found in ad-related URLs
// Ad Block hides links containing these keywords
// https://hyphengroup.atlassian.net/browse/CMS-391
const AdBlockKeywords = [
  'ad',
  'ads',
  'adv',
  'advert',
  'advertising',
  'adserver',
  'tracker',
  'analytics',
  'pixel',
  'counter',
  'doubleclick',
  'googleadservices',
  'googlesyndication',
  'adsense',
  'moatads',
  'openx.net',
  'pubmatic.com',
];

export const FeLink = forwardRef<HTMLAnchorElement, FeLinkProps>(
  (props, ref) => {
    const { href, children, onClickCapture, ...anotherProps } = props;

    const isRedirectUrlIsAd = AdBlockKeywords.some((keyword) =>
      href?.toLowerCase().includes(keyword),
    );

    const appendQueryParams = useAppendQueryParams();
    const formattedRef = appendQueryParams(href ?? '#');
    const onClickHandler = useMemo(() => {
      if (!isRedirectUrlIsAd) return undefined;

      return (event: ReactMouseEvent<HTMLElement>) => {
        // Run the consumer's capture handler first and honour it: a CTA may
        // intercept the click to do something else entirely (e.g. open a modal
        // instead of navigating). Before, this handler replaced the consumer's
        // one outright, so such an intercept was silently dropped for any URL
        // that trips an AdBlock keyword.
        (onClickCapture as MouseEventHandler<HTMLElement> | undefined)?.(event);
        if (event.defaultPrevented) return;

        window.open(formattedRef, '_blank', 'noopener,noreferrer');
      };
    }, [isRedirectUrlIsAd, formattedRef, onClickCapture]);

    /**
     * Keyboard equivalent of the click above. Dispatches a real click rather
     * than repeating the open logic, so a consumer's `onClickCapture` intercept
     * runs for the keyboard exactly as it does for the mouse.
     */
    const onKeyDownHandler = useMemo(() => {
      if (!isRedirectUrlIsAd) return undefined;

      return (event: ReactKeyboardEvent<HTMLSpanElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the page on anything that is not a real control.
        event.preventDefault();
        event.currentTarget.click();
      };
    }, [isRedirectUrlIsAd]);

    if (isRedirectUrlIsAd) {
      const { className, ...spanProps } = anotherProps;

      // Not an anchor, by design: an `href` containing an AdBlock keyword gets
      // the whole element hidden by the extension (CMS-391). The trade-off is
      // that a span carries none of what an anchor gives for free, so the
      // affordances are added back here rather than at each of the call sites:
      // the pointer cursor, and keyboard reach (QA on CMS-1937, AIO-021).
      // `role` and `tabIndex` sit before the spread so a consumer can still
      // override them.
      return (
        <span
          role="link"
          tabIndex={0}
          {...spanProps}
          className={classNames('cursor-pointer', className)}
          onClickCapture={onClickHandler}
          onKeyDown={onKeyDownHandler}
        >
          {children}
        </span>
      );
    }

    return (
      <a
        ref={ref}
        href={formattedRef}
        {...anotherProps}
        onClickCapture={onClickCapture}
      >
        {children}
      </a>
    );
  },
);

FeLink.displayName = 'FeLink';
